import React, { useEffect, useState, useRef, useCallback } from "react";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import LinkIcon from '@mui/icons-material/Link';
import Search from "../../../components/common/search/Search";


import {
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

// UPDATED: Use the same axios instance pattern as Todo List
import axiosInstance from "../../../utils/axios/axiosInstance";

// UPDATED: Use react-toastify like Todo List
import { toast } from 'react-toastify';

// UPDATED: Import loading gif like Todo List
import loadingGif from '../../../assets/images/loading.gif';

// Function to map admin icon names to emojis
const mapAdminIconToEmoji = (adminIcon) => {
  // Map admin icon names to specific emojis
  const iconMapping = {
    'library': '📚',
    'book': '📖',
    'computer': '💻',
    'mail': '📧',
    'calendar': '📅',
    'user': '👤',
    'settings': '⚙️',
    'home': '🏠',
    'phone': '📞',
    'map': '🗺️',
    'info': 'ℹ️',
    'help': '❓',
    'star': '⭐',
    'heart': '❤️',
    'shield': '🛡️'
  };

  // If adminIcon is null/undefined, return default
  if (!adminIcon) {
    return '🔗';
  }

  // If adminIcon is already an emoji, return it
  if (typeof adminIcon === 'string' && adminIcon.length <= 4) {
    // Check if it's likely an emoji (contains non-ASCII characters or is very short)
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(adminIcon)) {
      return adminIcon;
    }
  }

  // Convert to lowercase and try to map
  const iconKey = String(adminIcon).toLowerCase().trim();
  
  // Direct mapping
  if (iconMapping[iconKey]) {
    return iconMapping[iconKey];
  }

  // Partial matching - check if the icon name contains any of our keys
  for (const [key, emoji] of Object.entries(iconMapping)) {
    if (iconKey.includes(key) || key.includes(iconKey)) {
      return emoji;
    }
  }

  // Default fallback
  return '🔗';
};

const QuickLinksTab = () => {
  // Use ref to track if component is mounted
  const isMountedRef = useRef(true);

  // NEW: Tab state
  const [activeTab, setActiveTab] = useState('personal'); // 'general' or 'personal'

  // Personal Links State
  const [personalLinks, setPersonalLinks] = useState([]);
  const [personalLoading, setPersonalLoading] = useState(true);
  const [personalError, setPersonalError] = useState(null);

  // General Links State (NEW)
  const [generalLinks, setGeneralLinks] = useState([]);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null);

  // Shared UI State
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  // Filter and Pagination states
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Changed default for table view

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    category: 1,
    icon: '◻️',
    pinned: false
  });

  // Form validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [validationTouched, setValidationTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const categoryOptions = [
    { "id": 1, "name": "Academics" },
    { "id": 2, "name": "Student Services" },
    { "id": 3, "name": "Learning Resources" },
    { "id": 4, "name": "Career & Jobs" },
    { "id": 5, "name": "Campus Life" },
    { "id": 6, "name": "Research" },
    { "id": 7, "name": "Library" },
    { "id": 8, "name": "Finance" },
    { "id": 9, "name": "Health & Wellness" },
    { "id": 10, "name": "Technology Tools" },
    { "id": 11, "name": "Social Media" },
    { "id": 12, "name": "News & Updates" },
    { "id": 13, "name": "Entertainment" },
    { "id": 14, "name": "Productivity" },
    { "id": 15, "name": "Communication" }
  ];

  const iconOptions = [
    { value: "◻️", label: "Default" },
    { value: "📚", label: "Books" },
    { value: "🎓", label: "Graduation" },
    { value: "💻", label: "Computer" },
    { value: "🔗", label: "Link" },
    { value: "📖", label: "Book" },
    { value: "🌐", label: "Web" },
    { value: "📊", label: "Chart" },
    { value: "📝", label: "Note" },
    { value: "🎯", label: "Target" },
    { value: "⚡", label: "Lightning" },
    { value: "🚀", label: "Rocket" },
    { value: "💡", label: "Bulb" },
    { value: "🔍", label: "Search" },
    { value: "📱", label: "Mobile" },
    { value: "🎨", label: "Art" },
    { value: "🏆", label: "Trophy" },
    { value: "📈", label: "Growth" },
    { value: "🎪", label: "Entertainment" },
    { value: "🏥", label: "Health" },
    { value: "💰", label: "Money" }
  ];

  // Helper function to get current links and states based on active tab
  const getCurrentTabData = useCallback(() => {
    if (activeTab === 'general') {
      return {
        links: generalLinks,
        loading: generalLoading,
        error: generalError,
        setLinks: setGeneralLinks,
        setLoading: setGeneralLoading,
        setError: setGeneralError
      };
    } else {
      return {
        links: personalLinks,
        loading: personalLoading,
        error: personalError,
        setLinks: setPersonalLinks,
        setLoading: setPersonalLoading,
        setError: setPersonalError
      };
    }
  }, [activeTab, generalLinks, generalLoading, generalError, personalLinks, personalLoading, personalError]);

  // Form validation functions
  const validateTitle = useCallback((title) => {
    const trimmedTitle = title?.trim() || '';

    if (!trimmedTitle) {
      return 'Link title is required';
    }
    if (trimmedTitle.length < 2) {
      return 'Title must be at least 2 characters long';
    }
    if (trimmedTitle.length > 100) {
      return 'Title must be less than 100 characters';
    }
    return null;
  }, []);

  const validateUrl = useCallback((url) => {
    const trimmedUrl = url?.trim() || '';

    if (!trimmedUrl) {
      return 'URL is required';
    }
    
    try {
      new URL(trimmedUrl);
    } catch (e) {
      return 'Please enter a valid URL (e.g., https://example.com)';
    }

    if (trimmedUrl.length > 500) {
      return 'URL must be less than 500 characters';
    }

    return null;
  }, []);

  const validateCategory = useCallback((categoryId) => {
    if (!categoryId) {
      return 'Category is required';
    }
    
    const numericId = parseInt(categoryId);
    if (isNaN(numericId) || numericId < 1) {
      return 'Please select a valid category';
    }
    
    if (categoryOptions.length > 0 && !categoryOptions.find(cat => cat.id === numericId)) {
      return 'Selected category does not exist';
    }
    
    return null;
  }, []);

  const validateForm = useCallback((data = formData) => {
    const errors = {};

    const titleError = validateTitle(data.title);
    if (titleError) errors.title = titleError;

    const urlError = validateUrl(data.url);
    if (urlError) errors.url = urlError;

    const categoryError = validateCategory(data.category);
    if (categoryError) errors.category = categoryError;

    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsFormValid(isValid);

    return { isValid, errors };
  }, [formData, validateTitle, validateUrl, validateCategory]);

  const validateField = useCallback((fieldName, value) => {
    let error = null;

    switch (fieldName) {
      case 'title':
        error = validateTitle(value);
        break;
      case 'url':
        error = validateUrl(value);
        break;
      case 'category':
        error = validateCategory(value);
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error;
  }, [validateTitle, validateUrl, validateCategory]);

  const markFieldAsTouched = useCallback((fieldName) => {
    setValidationTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      title: '',
      url: '',
      category: 1,
      icon: '◻️',
      pinned: false
    });
    setValidationErrors({});
    setValidationTouched({});
    setIsFormValid(false);
    setEditMode(false);
    setEditingLink(null);
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field !== 'pinned') {
      markFieldAsTouched(field);
      validateField(field, value);
    }
  }, [markFieldAsTouched, validateField]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = useCallback(() => {
    return categoryFilter !== 'all' || searchQuery.trim() !== '';
  }, [categoryFilter, searchQuery]);

  const resetAllFilters = useCallback(() => {
    setCategoryFilter('all');
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (Object.keys(validationTouched).length > 0) {
      validateForm();
    }
  }, [formData, validateForm, validationTouched]);

  // NEW: Tab change handler
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
    resetAllFilters();
  }, [resetAllFilters]);

  // UPDATED: Single API call that separates personal and general links based on by_admin field
  const fetchAllLinks = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      // Set loading for both tabs since we're fetching all data
      setPersonalLoading(true);
      setGeneralLoading(true);
      setPersonalError(null);
      setGeneralError(null);

      console.log('📡 Fetching all quick links from API...');
      
      const response = await axiosInstance.get(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/quick-links`);
      
      if (!isMountedRef.current) return;

      console.log('📥 All Links API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const quickLinksData = response.data.data || [];
        
        // Separate links based on by_admin field
        const personalLinksData = [];
        const generalLinksData = [];
        
        quickLinksData.forEach(link => {
          const transformedLink = {
            id: link.id,
            title: link.title,
            url: link.url,
            category: link.category?.id || 1,
            categoryName: link.category?.name || 'Unknown',
            icon: link.by_admin === 1 ? mapAdminIconToEmoji(link.icon) : (link.icon || '◻️'),
            pinned: Boolean(link.pinned),
            created_at: link.created_at || new Date().toISOString(),
            updated_at: link.updated_at || new Date().toISOString(),
            by_admin: link.by_admin || 0
          };
          
          if (link.by_admin === 1) {
            generalLinksData.push(transformedLink);
          } else {
            personalLinksData.push(transformedLink);
          }
        });

        setPersonalLinks(personalLinksData);
        setGeneralLinks(generalLinksData);
        
        toast.success(`Loaded ${personalLinksData.length} personal and ${generalLinksData.length} general links successfully!`, {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to fetch quick links');
      }
    } catch (err) {
      console.error('❌ Error fetching quick links:', err);
      
      let errorMessage = 'Failed to load quick links';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setPersonalError(errorMessage);
        setGeneralError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setPersonalLoading(false);
        setGeneralLoading(false);
      }
    }
  }, []);

  // Create new quick link (only works for personal links)
  const createQuickLink = useCallback(async () => {
    if (!isMountedRef.current) return;

    const requiredFields = ['title', 'url', 'category'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    const { isValid, errors } = validateForm();

    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    try {
      setSubmitting(true);
      setPersonalError(null);

      const linkData = {
        title: formData.title.trim(),
        url: formData.url.trim(),
        category: String(formData.category),
        icon: formData.icon || '◻️',
        pinned: formData.pinned
      };

      console.log('📤 Creating personal quick link:', linkData);

      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/quick-links`, linkData);

      if (!isMountedRef.current) return;

      console.log('📥 Create Personal API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const newLink = response.data.data;
        
        const transformedLink = {
          id: newLink.id,
          title: newLink.title,
          url: newLink.url,
          category: newLink.category?.id || parseInt(formData.category),
          categoryName: newLink.category?.name || categoryOptions.find(cat => cat.id === parseInt(formData.category))?.name || 'Unknown',
          icon: newLink.icon || '◻️',
          pinned: Boolean(newLink.pinned),
          created_at: newLink.created_at || new Date().toISOString(),
          updated_at: newLink.updated_at || new Date().toISOString(),
          by_admin: newLink.by_admin || 0
        };

        setPersonalLinks(prev => [transformedLink, ...prev]);
        setOpen(false);
        resetForm();

        toast.success(
          formData.pinned ? 'Personal link created and pinned!' : 'Personal link created successfully!', {
            position: "top-center",
            autoClose: 3000,
          }
        );
      } else {
        throw new Error(response.data?.message || 'Failed to create personal link');
      }
    } catch (err) {
      console.error('❌ Error creating personal link:', err);
      
      let errorMessage = 'Failed to create personal link';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errors = err.response.data.errors;
          errorMessage = errors.join(', ');
        }
      } else if (err.response?.data?.errors) {
        if (Array.isArray(err.response.data.errors)) {
          errorMessage = err.response.data.errors.join(', ');
        } else {
          errorMessage = err.response.data.errors;
        }
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setPersonalError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [formData, resetForm, markFieldAsTouched, validateForm, categoryOptions]);

  // Update quick link function (only works for personal links)
  const updateQuickLink = useCallback(async () => {
    if (!isMountedRef.current || !editingLink) return;

    const requiredFields = ['title', 'url', 'category'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    const { isValid, errors } = validateForm();

    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    try {
      setSubmitting(true);
      setPersonalError(null);

      const linkData = {
        id: editingLink.id,
        title: formData.title.trim(),
        url: formData.url.trim(),
        category: String(formData.category),
        icon: formData.icon || '◻️',
        pinned: formData.pinned
      };

      console.log('📤 Updating personal quick link:', linkData);

      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/quick-links/edit`, linkData);

      if (!isMountedRef.current) return;

      console.log('📥 Update Personal API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const updatedLink = response.data.data;
        
        const processedLink = {
          id: updatedLink.id,
          title: updatedLink.title,
          url: updatedLink.url,
          category: updatedLink.category?.id || parseInt(formData.category),
          categoryName: updatedLink.category?.name || categoryOptions.find(cat => cat.id === parseInt(formData.category))?.name || 'Unknown',
          icon: updatedLink.icon || '◻️',
          pinned: Boolean(updatedLink.pinned),
          created_at: updatedLink.created_at || editingLink.created_at,
          updated_at: updatedLink.updated_at || new Date().toISOString(),
          by_admin: updatedLink.by_admin || 0
        };

        setPersonalLinks(prev => prev.map(link =>
          link.id === editingLink.id ? processedLink : link
        ));

        setOpen(false);
        resetForm();

        toast.success('Personal link updated successfully!', {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to update personal link');
      }
    } catch (err) {
      console.error('❌ Error updating personal link:', err);
      
      let errorMessage = 'Failed to update personal link';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errors = err.response.data.errors;
          errorMessage = errors.join(', ');
        }
      } else if (err.response?.data?.errors) {
        if (Array.isArray(err.response.data.errors)) {
          errorMessage = err.response.data.errors.join(', ');
        } else {
          errorMessage = err.response.data.errors;
        }
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setPersonalError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [formData, editingLink, resetForm, markFieldAsTouched, validateForm, categoryOptions]);

  // Delete quick link (only works for personal links)
  const deleteQuickLink = useCallback(async (linkId) => {
    if (!linkId || !isMountedRef.current) return;

    try {
      console.log('📤 Deleting personal quick link:', linkId);

      const response = await axiosInstance.delete(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/quick-links/${linkId}`);

      if (!isMountedRef.current) return;

      console.log('📥 Delete Personal API Response:', response.data);

      if (response.data?.status === 200 || response.status === 200) {
        setPersonalLinks(prev => prev.filter(link => link.id !== linkId));
        
        toast.success('Personal link deleted successfully!', {
          position: "top-center",
          autoClose: 2000,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to delete personal link');
      }
    } catch (err) {
      console.error('❌ Error deleting personal link:', err);

      let errorMessage = 'Failed to delete personal link';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  }, []);

  // Toggle pin status (only works for personal links)
  const togglePinLink = useCallback(async (linkId, currentPinnedStatus) => {
    if (!linkId || !isMountedRef.current) return;

    try {
      const newPinnedStatus = !currentPinnedStatus;

      console.log('📤 Toggling personal pin status:', { linkId, newPinnedStatus });

      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/quick-links/pin/${linkId}`);

      if (!isMountedRef.current) return;

      console.log('📥 Pin Personal API Response:', response.data);

      if (response.data?.status === 200 || response.status === 200) {
        setPersonalLinks(prev => prev.map(link =>
          link.id === linkId ? { ...link, pinned: newPinnedStatus } : link
        ));

        toast.success(
          newPinnedStatus ? 'Personal link pinned to top!' : 'Personal link unpinned!', {
            position: "top-center",
            autoClose: 2000,
          }
        );
      } else {
        throw new Error(response.data?.message || 'Failed to update pin status');
      }
    } catch (err) {
      console.error('❌ Error toggling personal pin status:', err);
      
      let errorMessage = 'Failed to update pin status';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  }, []);

  // Initialize component - load all links on mount
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      await fetchAllLinks();
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [fetchAllLinks]);

  // Handle form submission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    console.log('📝 Form submission:', formData);
    console.log('📝 Edit mode:', editMode);

    if (editMode) {
      updateQuickLink();
    } else {
      createQuickLink();
    }
  }, [formData, editMode, createQuickLink, updateQuickLink]);

  // Handle edit quick link (only for personal links)
  const handleEdit = useCallback((link) => {
    setEditMode(true);
    setEditingLink(link);

    const editFormData = {
      title: link.title || '',
      url: link.url || '',
      category: parseInt(link.category) || 1,
      icon: link.icon || '◻️',
      pinned: Boolean(link.pinned)
    };

    setFormData(editFormData);
    setValidationErrors({});
    setValidationTouched({});

    setTimeout(() => {
      validateForm(editFormData);
    }, 100);

    setOpen(true);
  }, [validateForm]);

  // Handle open link
  const handleOpenLink = useCallback((url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper function to get category info
  const getCategoryInfo = useCallback((categoryId) => {
    const category = categoryOptions.find(cat => cat.id === parseInt(categoryId));
    const categoryName = category ? category.name.toLowerCase() : '';
    let color = '#795548';
    let bgColor = '#efebe9';

    switch (categoryName) {
      case 'academics':
        color = '#3f51b5';
        bgColor = '#e8eaf6';
        break;
      case 'student services':
        color = '#ff9800';
        bgColor = '#fff3e0';
        break;
      case 'learning resources':
        color = '#4caf50';
        bgColor = '#e8f5e9';
        break;
      case 'career & jobs':
        color = '#f44336';
        bgColor = '#ffebee';
        break;
      case 'campus life':
        color = '#9c27b0';
        bgColor = '#f3e5f5';
        break;
      case 'research':
        color = '#2196f3';
        bgColor = '#e3f2fd';
        break;
      case 'library':
        color = '#673ab7';
        bgColor = '#ede7f6';
        break;
      case 'finance':
        color = '#ff5722';
        bgColor = '#fbe9e7';
        break;
      case 'health & wellness':
        color = '#607d8b';
        bgColor = '#eceff1';
        break;
      case 'technology tools':
        color = '#009688';
        bgColor = '#e0f2f1';
        break;
      case 'social media':
        color = '#795548';
        bgColor = '#efebe9';
        break;
      case 'news & updates':
        color = '#8bc34a';
        bgColor = '#f1f8e9';
        break;
      case 'entertainment':
        color = '#4caf50';
        bgColor = '#e8f5e9';
        break;
      case 'productivity':
        color = '#3f51b5';
        bgColor = '#e8eaf6';
        break;
      case 'communication':
        color = '#ff9800';
        bgColor = '#fff3e0';
        break;
      default:
        break;
    }

    return {
      label: category ? category.name : 'Unknown Category',
      color,
      bgColor
    };
  }, []);

  const handleAddNew = useCallback(() => {
    setEditMode(false);
    setEditingLink(null);
    resetForm();
    setOpen(true);
  }, [resetForm]);

  const closeSlideWindow = useCallback(() => {
    setOpen(false);
    resetForm();
    
    // Clear errors for personal tab only (since only personal links can be edited)
    setPersonalError(null);
  }, [resetForm]);

  // Filter and sort quick links with pinned links at top
  const getFilteredAndSortedLinks = useCallback(() => {
    const { links } = getCurrentTabData();
    let filteredLinks = [...links];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredLinks = filteredLinks.filter(link => {
        const titleMatch = link.title?.toLowerCase().includes(query);
        const urlMatch = link.url?.toLowerCase().includes(query);
        const categoryMatch = link.categoryName?.toLowerCase().includes(query);
        return titleMatch || urlMatch || categoryMatch;
      });
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      const categoryId = parseInt(categoryFilter);
      filteredLinks = filteredLinks.filter(link => parseInt(link.category) === categoryId);
    }

    filteredLinks.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    return filteredLinks;
  }, [getCurrentTabData, categoryFilter, searchQuery]);

  const getPaginatedLinks = useCallback(() => {
    const filteredLinks = getFilteredAndSortedLinks();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLinks.slice(startIndex, endIndex);
  }, [getFilteredAndSortedLinks, currentPage, itemsPerPage]);

  const filteredAndSortedLinks = getFilteredAndSortedLinks();
  const totalPages = Math.ceil(filteredAndSortedLinks.length / itemsPerPage);
  const totalItems = filteredAndSortedLinks.length;

  const handlePageChange = useCallback((value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getFieldError = useCallback((fieldName) => {
    return validationTouched[fieldName] && validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  const isFieldValid = useCallback((fieldName) => {
    return validationTouched[fieldName] && !validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  const truncateText = useCallback((text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }, []);

  const truncateUrl = useCallback((url, maxLength = 40) => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      if (domain.length <= maxLength) return domain;
      return domain.substring(0, maxLength - 3) + '...';
    } catch (e) {
      return url.substring(0, maxLength - 3) + '...';
    }
  }, []);

  const { links: currentLinks, loading: currentLoading, error: currentError } = getCurrentTabData();

  // Loading state
  if (currentLoading && currentLinks.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          {loadingGif ? (
            <img
              src={loadingGif}
              alt="Loading..."
              style={{ width: '80px', marginBottom: '16px' }}
            />
          ) : (
            <div style={{ marginBottom: '16px', fontSize: '48px' }}>⏳</div>
          )}
          <div style={{ fontSize: '20px', color: '#666', fontFamily: '"Saira", sans-serif' }}>
            Loading your {activeTab} links...
          </div>
        </div>
      </div>
    );
  }

  // Error state (only show if there are no links to display)
  if (currentError && currentLinks.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ padding: '32px', maxWidth: '400px', margin: '0 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', color: '#f44336', marginBottom: '16px' }}>❌</div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#ffebee', 
            border: '1px solid #f44336',
            borderRadius: '8px',
            marginBottom: '24px',
            fontFamily: '"Saira", sans-serif'
          }}>
            <strong>Error!</strong> {currentError}
          </div>
          <button
            onClick={fetchAllLinks}
            style={{
              padding: '12px 24px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const paginatedLinks = getPaginatedLinks();

  return (
    <div style={{ padding: '16px' }}>
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                {/* Error display */}
                {currentError && currentLinks.length > 0 && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#ffebee',
                    border: '1px solid #f44336',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    fontFamily: '"Saira", sans-serif',
                    color: '#c62828'
                  }}>
                    <strong>Error!</strong> {currentError}
                    <button
                      onClick={() => {
                        const { setError } = getCurrentTabData();
                        setError(null);
                      }}
                      style={{
                        float: 'right',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#c62828'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Header Section */}
                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-2" style={{ fontFamily: '"Saira", sans-serif' }}>
                      Quick Links
                      {currentLoading && <span style={{ marginLeft: '8px', fontSize: '16px' }}>⏳</span>}
                    </h3>
                  </div>
                  <div className="d-flex align-items-center flex-wrap mb-1">
                    {/* Only show Add New button for Personal Links */}
                    {activeTab === 'personal' && (
                      <button 
                        onClick={handleAddNew}
                        className="!w-fit !px-3 !h-10"
                        disabled={submitting}
                        style={{
                          backgroundColor: '#ce55b9',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '2px 24px',
                          cursor: submitting ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontFamily: '"Saira", sans-serif',
                          opacity: submitting ? 0.6 : 1
                        }}
                      >
                        {submitting ? 'Processing...' : '+ Add New Personal Link'}
                      </button>
                    )}
                  </div>
                </div>

                {/* NEW: Tab Navigation */}
                <div style={{ 
                  marginBottom: '32px',
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: '0'
                }}>
                  <div style={{ display: 'flex', gap: '0' }}>
                    <button
                      onClick={() => handleTabChange('personal')}
                      style={{
                        padding: '16px 32px',
                        backgroundColor: activeTab === 'personal' ? '#ce55b9' : 'transparent',
                        color: activeTab === 'personal' ? 'white' : '#666',
                        border: 'none',
                        borderBottom: activeTab === 'personal' ? '3px solid #ce55b9' : '3px solid transparent',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        fontFamily: '"Saira", sans-serif',
                        borderRadius: '8px 8px 0 0',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        marginBottom: '-1px'
                      }}
                    >
                      Personal Links ({personalLinks.length})
                    </button>
                    <button
                      onClick={() => handleTabChange('general')}
                      style={{
                        padding: '16px 32px',
                        backgroundColor: activeTab === 'general' ? '#ce55b9' : 'transparent',
                        color: activeTab === 'general' ? 'white' : '#666',
                        border: 'none',
                        borderBottom: activeTab === 'general' ? '3px solid #ce55b9' : '3px solid transparent',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        fontFamily: '"Saira", sans-serif',
                        borderRadius: '8px 8px 0 0',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        marginBottom: '-1px'
                      }}
                    >
                      Admin Generated Links ({generalLinks.length})
                    </button>
                  </div>
                </div>

                {/* Filter Panel */}
                <div className="row filter-panel mt-4">
                  <div className="col-md-3">
                    <div className="serarch-bar">
                      <Search
                        value={searchQuery}
                        handleInputChange={(e) => handleSearchChange(e)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <FormControl
  size="small"
  sx={{
    minWidth: '100%',
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ce55b9',
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#ce55b9'
    }
  }}
>
  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>
    Category
  </InputLabel>
  <Select
    value={categoryFilter}
    label="Category"
    onChange={(e) => setCategoryFilter(e.target.value)}
    sx={{
      fontFamily: '"Saira", sans-serif',
      fontWeight: 500,
      '& .MuiSelect-select': {
        fontFamily: '"Saira", sans-serif'
      }
    }}
  >
    <MenuItem value="all" sx={{ fontFamily: '"Saira", sans-serif' }}>
      All Categories ({currentLinks.length})
    </MenuItem>
    {categoryOptions.map((category) => (
      <MenuItem
        key={category.id}
        value={category.id.toString()}
        sx={{ fontFamily: '"Saira", sans-serif' }}
      >
        {category.name} ({currentLinks.filter(link => parseInt(link.category) === category.id).length})
      </MenuItem>
    ))}
  </Select>
</FormControl>
                  </div>
                  <div className="col-md-3">
                    <FormControl
  size="small"
  sx={{
    minWidth: '100%',
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ce55b9',
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#ce55b9'
    }
  }}
>
  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>
    Per Page
  </InputLabel>
  <Select
    value={itemsPerPage}
    label="Per Page"
    onChange={(e) => {
      setItemsPerPage(parseInt(e.target.value));
      setCurrentPage(1);
    }}
    sx={{
      fontFamily: '"Saira", sans-serif',
      fontWeight: 500,
      '& .MuiSelect-select': {
        fontFamily: '"Saira", sans-serif'
      }
    }}
  >
    <MenuItem value={5} sx={{ fontFamily: '"Saira", sans-serif' }}>
      5 per page
    </MenuItem>
    <MenuItem value={10} sx={{ fontFamily: '"Saira", sans-serif' }}>
      10 per page
    </MenuItem>
    <MenuItem value={20} sx={{ fontFamily: '"Saira", sans-serif' }}>
      20 per page
    </MenuItem>
    <MenuItem value={50} sx={{ fontFamily: '"Saira", sans-serif' }}>
      50 per page
    </MenuItem>
  </Select>
</FormControl>
                  </div>
                  <div className="col-md-3">
                    {hasActiveFilters() && (
                      <button
                        onClick={resetAllFilters}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontFamily: '"Saira", sans-serif',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                          justifyContent: 'center'
                        }}
                      >
                        🔄 Reset Filters
                      </button>
                    )}
                  </div>
                </div>

                {/* Table View */}
                <div style={{ marginTop: '32px' }}>
                  {paginatedLinks.length > 0 ? (
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                      border: '1px solid #e5e7eb'
                    }}>
                      {/* Table Header */}
                      <div style={{
  display: 'grid',
  gridTemplateColumns: '40px 1fr 200px 150px 120px 200px',
  gap: '16px',
gap: '16px',
                        padding: '20px 24px',
                        backgroundColor: '#f8fafc',
                        borderBottom: '2px solid #e5e7eb',
                        fontFamily: '"Saira", sans-serif',
                        fontWeight: '700',
                        fontSize: '14px',
                        color: '#374151',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
}}>
  <div>Icon</div>
  <div>Title</div>
  <div>Category</div>
  <div>URL</div>
  <div>Created</div>
  <div style={{ textAlign: 'center' }}>Actions</div>
</div>

                      {/* Table Body */}
                      {paginatedLinks.map((link, index) => {
                        const categoryInfo = getCategoryInfo(link.category);
                        const isPersonalLink = activeTab === 'personal';
                        const isEvenRow = index % 2 === 0;

                        return (
                          <div
                            key={link.id}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '40px 1fr 200px 150px 120px 200px',
                              gap: '16px',
                              padding: '16px 24px',
                              backgroundColor: link.pinned 
                                ? 'rgba(25, 118, 210, 0.05)' 
                                : isEvenRow 
                                  ? 'white' 
                                  : '#fafbfc',
                              borderBottom: '1px solid #f1f5f9',
                              borderLeft: link.pinned ? '4px solid #1976d2' : '4px solid transparent',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              cursor: 'default'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = link.pinned 
                                ? 'rgba(25, 118, 210, 0.1)' 
                                : '#f8fafc';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = link.pinned 
                                ? 'rgba(25, 118, 210, 0.05)' 
                                : isEvenRow 
                                  ? 'white' 
                                  : '#fafbfc';
                            }}
                          >
                            {/* Status Column */}
                           
                           {/* Icon Column */}
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <span style={{ fontSize: '20px' }}>{link.icon}</span>
  </div>

                            {/* Title Column */}
                            <div>
                              <div style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                color: '#1f2937',
                                fontFamily: '"Saira", sans-serif',
                                marginBottom: '2px',
                                wordBreak: 'break-word'
                              }}>
                                {truncateText(link.title || 'Untitled Link', 60)}
                              </div>
                            </div>

                            {/* Category Column */}
                            <div>
                              <span style={{
                                backgroundColor: categoryInfo.bgColor,
                                color: categoryInfo.color,
                                padding: '4px 10px',
                                borderRadius: '16px',
                                fontSize: '11px',
                                fontWeight: '600',
                                fontFamily: '"Saira", sans-serif',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                border: `1px solid ${categoryInfo.color}20`,
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                              }}>
                                {truncateText(categoryInfo.label, 15)}
                              </span>
                            </div>

                            {/* URL Column */}
                            <div 
                              onClick={() => handleOpenLink(link.url)}
                              style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 10px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e2e8f0';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8fafc';
                              }}
                            >
                              <LinkIcon sx={{ fontSize: '14px', color: '#6b7280' }} />
                              <span style={{
                                fontSize: '13px',
                                color: '#4b5563',
                                fontFamily: '"Saira", sans-serif',
                                fontWeight: '500',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {truncateUrl(link.url, 20)}
                              </span>
                            </div>

                            {/* Created Date Column */}
                            <div style={{
                              fontSize: '12px',
                              color: '#64748b',
                              fontFamily: '"Saira", sans-serif',
                              fontWeight: '500'
                            }}>
                              {new Date(link.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: '2-digit'
                              })}
                            </div>

                            {/* Actions Column */}
                            <div style={{
                              display: 'flex',
                              gap: '6px',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}>
                              <button
                                onClick={() => handleOpenLink(link.url)}
                                style={{
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  width: '28px',
                                  height: '28px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease'
                                }}
                                title="Open Link"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#059669';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#10b981';
                                }}
                              >
                                <LaunchIcon sx={{ fontSize: '14px' }} />
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(link.url);
                                  toast.success('URL copied!', {
                                    position: "top-center",
                                    autoClose: 2000,
                                  });
                                }}
                                style={{
                                  backgroundColor: '#6366f1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  width: '28px',
                                  height: '28px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  transition: 'all 0.2s ease'
                                }}
                                title="Copy URL"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#4f46e5';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#6366f1';
                                }}
                              >
                                📋
                              </button>

                              {/* Only show pin/edit/delete for personal links */}
                              {isPersonalLink && (
                                <>
                                  <button
                                    onClick={() => togglePinLink(link.id, link.pinned)}
                                    disabled={submitting || currentLoading}
                                    style={{
                                      backgroundColor: link.pinned ? '#1976d2' : '#f3f4f6',
                                      color: link.pinned ? 'white' : '#6b7280',
                                      border: link.pinned ? 'none' : '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      width: '28px',
                                      height: '28px',
                                      cursor: submitting || currentLoading ? 'not-allowed' : 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease',
                                      opacity: submitting || currentLoading ? 0.6 : 1
                                    }}
                                    title={link.pinned ? 'Unpin Link' : 'Pin Link'}
                                    onMouseEnter={(e) => {
                                      if (!link.pinned && !submitting && !currentLoading) {
                                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!link.pinned && !submitting && !currentLoading) {
                                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                                      }
                                    }}
                                  >
                                    {link.pinned ? 
                                      <PushPinIcon sx={{ fontSize: '14px' }} /> : 
                                      <PushPinOutlinedIcon sx={{ fontSize: '14px' }} />
                                    }
                                  </button>

                                  <button
                                    onClick={() => handleEdit(link)}
                                    disabled={submitting || currentLoading}
                                    style={{
                                      backgroundColor: '#f59e0b',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      width: '28px',
                                      height: '28px',
                                      cursor: submitting || currentLoading ? 'not-allowed' : 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease',
                                      opacity: submitting || currentLoading ? 0.6 : 1
                                    }}
                                    title="Edit Link"
                                    onMouseEnter={(e) => {
                                      if (!submitting && !currentLoading) {
                                        e.currentTarget.style.backgroundColor = '#d97706';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!submitting && !currentLoading) {
                                        e.currentTarget.style.backgroundColor = '#f59e0b';
                                      }
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: '14px' }} />
                                  </button>

                                  <button
                                    onClick={() => deleteQuickLink(link.id)}
                                    disabled={submitting || currentLoading}
                                    style={{
                                      backgroundColor: '#ef4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      width: '28px',
                                      height: '28px',
                                      cursor: submitting || currentLoading ? 'not-allowed' : 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease',
                                      opacity: submitting || currentLoading ? 0.6 : 1
                                    }}
                                    title="Delete Link"
                                    onMouseEnter={(e) => {
                                      if (!submitting && !currentLoading) {
                                        e.currentTarget.style.backgroundColor = '#dc2626';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!submitting && !currentLoading) {
                                        e.currentTarget.style.backgroundColor = '#ef4444';
                                      }
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: '14px' }} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* No Links Empty State */
                    <div style={{
                      padding: '80px 32px',
                      textAlign: 'center',
                      backgroundColor: '#fafbfc',
                      borderRadius: '20px',
                      border: '2px dashed #e2e8f0',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)',
                        opacity: 0.1
                      }} />
                      
                      <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{ 
                          fontSize: '72px', 
                          marginBottom: '24px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}>
                          {activeTab === 'general' ? '🌐' : '🔗'}
                        </div>
                        
                        <h4 style={{ 
                          fontSize: '28px', 
                          fontWeight: '700', 
                          color: '#1f2937', 
                          marginBottom: '16px',
                          fontFamily: '"Saira", sans-serif'
                        }}>
                          {searchQuery ? 'No matching links found' : `No ${activeTab} links found`}
                        </h4>
                        
                        <p style={{ 
                          fontSize: '16px', 
                          color: '#6b7280', 
                          marginBottom: '40px', 
                          maxWidth: '500px', 
                          margin: '0 auto 40px auto',
                          fontFamily: '"Saira", sans-serif',
                          lineHeight: '1.6'
                        }}>
                          {searchQuery ?
                            `No ${activeTab} links match your search "${searchQuery}". Try adjusting your search terms or filters.` :
                            categoryFilter !== 'all' ?
                              `No ${activeTab} links match your current filter criteria. Try adjusting your filters or create a new link to get started.` :
                              activeTab === 'general' ?
                                'No general links available yet. These are typically managed by your institution.' :
                                'You haven\'t created any personal links yet. Start building your personalized link collection by creating your first link.'
                          }
                        </p>
                        
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {/* Only show create button for personal links */}
                          {activeTab === 'personal' && (
                            <button
                              onClick={handleAddNew}
                              disabled={submitting}
                              style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '16px 32px',
                                cursor: submitting ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontWeight: '600',
                                fontFamily: '"Saira", sans-serif',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                opacity: submitting ? 0.6 : 1,
                                transition: 'all 0.3s ease',
                                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.25)'
                              }}
                            >
                              <span style={{ fontSize: '20px' }}>✨</span>
                              {submitting ? 'Processing...' : (searchQuery ? 'Create New Link' : 'Create Your First Link')}
                            </button>
                          )}
                          
                          {(categoryFilter !== 'all' || searchQuery) && (
                            <button
                              onClick={resetAllFilters}
                              style={{
                                backgroundColor: 'white',
                                color: '#6b7280',
                                border: '2px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '16px 32px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600',
                                fontFamily: '"Saira", sans-serif',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <span style={{ fontSize: '20px' }}>🔄</span>
                              Show All Links
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Results Summary */}
                {paginatedLinks.length > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 24px',
                    backgroundColor: '#f8fafc',
                    borderTop: '1px solid #e5e7eb',
                    fontSize: '14px',
                    fontFamily: '"Saira", sans-serif',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>
                    <div>
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} {activeTab} links
                    </div>
                    <div>
                      Page {currentPage} of {totalPages}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="row mt-4">
                <div className="col-12 d-flex justify-content-center">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: '10px 16px',
                          backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                          color: currentPage === 1 ? '#ccc' : '#333',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          fontFamily: '"Saira", sans-serif',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ← Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 7) {
                            pageNum = i + 1;
                          } else if (currentPage <= 4) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 3) {
                            pageNum = totalPages - 6 + i;
                          } else {
                            pageNum = currentPage - 3 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              style={{
                                padding: '10px 12px',
                                backgroundColor: currentPage === pageNum ? '#ce55b9' : '#fff',
                                color: currentPage === pageNum ? 'white' : '#333',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontFamily: '"Saira", sans-serif',
                                fontWeight: currentPage === pageNum ? '600' : '500',
                                minWidth: '44px',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '10px 16px',
                          backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                          color: currentPage === totalPages ? '#ccc' : '#333',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          fontFamily: '"Saira", sans-serif',
                          fontWeight: '500',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal - Only show for personal links */}
      {open && activeTab === 'personal' && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '450px',
          maxWidth: '90vw',
          backgroundColor: 'white',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Saira", sans-serif'
        }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{
              background: '#ce55b9',
              color: 'white',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h5 style={{ 
                  fontSize: '20px', 
                  fontWeight: 600, 
                  fontFamily: '"Saira", sans-serif',
                  margin: 0
                }}>
                  {editMode ? 'Edit Personal Link' : 'Add New Personal Link'}
                </h5>
              </div>
              <button
                onClick={closeSlideWindow}
                disabled={submitting}
                style={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              padding: '24px',
              overflow: 'auto',
              background: '#ffffff'
            }}>
              {/* Title Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Link Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  onBlur={() => markFieldAsTouched('title')}
                  disabled={submitting}
                  placeholder="Enter a descriptive title for your link"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: getFieldError('title') ? '2px solid #f44336' : 
                           isFieldValid('title') ? '2px solid #4caf50' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: getFieldError('title') ? 'rgba(244, 67, 54, 0.05)' :
                                   isFieldValid('title') ? 'rgba(76, 175, 80, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: getFieldError('title') ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {getFieldError('title') || 'Enter a descriptive title for your link (2-100 characters)'}
                </div>
              </div>

              {/* URL Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => handleInputChange('url', e.target.value)}
                  onBlur={() => markFieldAsTouched('url')}
                  disabled={submitting}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: getFieldError('url') ? '2px solid #f44336' : 
                           isFieldValid('url') ? '2px solid #4caf50' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: getFieldError('url') ? 'rgba(244, 67, 54, 0.05)' :
                                   isFieldValid('url') ? 'rgba(76, 175, 80, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: getFieldError('url') ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {getFieldError('url') || 'Enter a valid URL starting with https:// or http://'}
                </div>
              </div>

              {/* Category Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Category *
                </label>
                <FormControl 
  fullWidth 
  error={!!getFieldError('category')}
  sx={{
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      fontFamily: '"Saira", sans-serif',
      '&.Mui-error': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#f44336',
        }
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: isFieldValid('category') ? '#4caf50' : '#ce55b9',
        }
      }
    },
    '& .MuiInputLabel-root': {
      fontFamily: '"Saira", sans-serif',
      '&.Mui-focused': {
        color: isFieldValid('category') ? '#4caf50' : '#ce55b9'
      }
    }
  }}
>
  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Category</InputLabel>
  <Select
    value={formData.category}
    label="Category"
    onChange={(e) => handleInputChange('category', e.target.value)}
    onBlur={() => markFieldAsTouched('category')}
    disabled={submitting}
    sx={{
      fontFamily: '"Saira", sans-serif',
      '& .MuiSelect-select': {
        fontFamily: '"Saira", sans-serif'
      }
    }}
  >
    {categoryOptions.map((category) => (
      <MenuItem
        key={category.id}
        value={category.id}
        sx={{ fontFamily: '"Saira", sans-serif' }}
      >
        {category.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: getFieldError('category') ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {getFieldError('category') || 'Select the category that best describes your link'}
                </div>
              </div>

              <div style={{
                height: '1px',
                backgroundColor: '#ddd',
                margin: '24px 0'
              }} />

              {/* Icon Picker Section */}
              <div style={{
                padding: '24px',
                backgroundColor: 'rgba(206, 85, 185, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(206, 85, 185, 0.2)'
              }}>
                <div style={{
                  marginBottom: '16px',
                  fontWeight: 600,
                  color: '#ce55b9',
                  fontFamily: '"Saira", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}>
                  🎨 Link Category Icon
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    fontSize: '20px'
                  }}>
                    {formData.icon || '🔗'}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#666',
                    fontFamily: '"Saira", sans-serif'
                  }}>
                    Selected: {iconOptions.find(icon => icon.value === formData.icon)?.label || 'Default'}
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  {iconOptions.map((icon) => (
                    <button
                      key={icon.value}
                      onClick={() => handleInputChange('icon', icon.value)}
                      disabled={submitting}
                      style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: formData.icon === icon.value ? '#ce55b9' : '#f5f5f5',
                        border: formData.icon === icon.value ? '2px solid #ce55b9' : '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        fontSize: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        opacity: submitting ? 0.6 : 1
                      }}
                      title={icon.label}
                    >
                      {icon.value}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div style={{
              padding: '24px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              background: 'white',
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeSlideWindow}
                disabled={submitting}
                style={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '100px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || (!isFormValid && Object.keys(validationTouched).length > 0)}
                style={{
                  background: isFormValid ? '#ce55b9' : '#ce55b9',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '140px',
                  color: 'white',
                  border: 'none',
                  cursor: submitting || (!isFormValid && Object.keys(validationTouched).length > 0) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: submitting || (!isFormValid && Object.keys(validationTouched).length > 0) ? 0.6 : 1
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ fontFamily: '"Saira", sans-serif' }}>
                      {editMode ? 'Updating...' : 'Creating...'}
                    </span>
                  </>
                ) : (
                  <>
                    {isFormValid && <span style={{ fontSize: '16px' }}>✓</span>}
                    <span style={{ fontFamily: '"Saira", sans-serif' }}>
                      {editMode ? 'Update Link' : 'Add Link'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {open && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200
          }}
          onClick={closeSlideWindow}
        />
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default QuickLinksTab;